import { format, isValid, parseISO } from 'date-fns';
import type {
  AgedInventorySkuRow,
  AgedInventoryStyleRow,
  AgingTierKey,
} from '../../../services/reports/AgedInventory.service';
import {
  DEFAULT_TOP_SELLERS_DATE_RANGE,
  TOP_SELLERS_DATE_RANGE_PRESETS,
  normalizeVariantColorSize,
  resolveDateRange,
} from '../topSellers/TopSellersReport.component';
import { csvCell } from '../vendorPerformance/VendorPerformanceReport.component';

// --- Aging tiers ---------------------------------------------------------------------------------

export interface AgingTierConfig {
  key: AgingTierKey;
  name: string;
  rangeLabel: string;
  // Inclusive lower bound in days; the tier runs up to the next tier's minDays - 1.
  minDays: number;
  badgeClass: string;
}

/**
 * Design defaults, in ascending order. Day 90 is still green, 180 blue and 365 amber; red starts
 * the day after a full year on hand. The server applies the same bounds for the Aging tier filter.
 */
export const AGING_TIERS: readonly AgingTierConfig[] = [
  {
    key: 'fresh',
    name: 'Fresh',
    rangeLabel: '0–90 days',
    minDays: 0,
    badgeClass: 'bg-[#ECFDF3] text-[#067647] border border-[#ABEFC6]',
  },
  {
    key: 'moderate',
    name: 'Moderate',
    rangeLabel: '91–180 days',
    minDays: 91,
    badgeClass: 'bg-[#EFF8FF] text-[#175CD3] border border-[#B2DDFF]',
  },
  {
    key: 'warning',
    name: 'Warning',
    rangeLabel: '181–365 days',
    minDays: 181,
    badgeClass: 'bg-[#FFFAEB] text-[#B54708] border border-[#FEDF89]',
  },
  {
    key: 'critical',
    name: 'Critical',
    rangeLabel: '366+ days',
    minDays: 366,
    badgeClass: 'bg-[#FEF3F2] text-[#B42318] border border-[#FECDCA]',
  },
];

const TIER_BY_KEY = new Map<AgingTierKey, AgingTierConfig>(AGING_TIERS.map((tier) => [tier.key, tier]));

export const agingTierByKey = (key: AgingTierKey): AgingTierConfig | undefined => TIER_BY_KEY.get(key);

export const agingTierLabel = (tier: AgingTierConfig): string => `${tier.name} (${tier.rangeLabel})`;

/** The tier an age in days falls in; null when the age is unknown (no usable receive date). */
export function getAgingTier(days: number | null | undefined): AgingTierConfig | null {
  if (days === null || days === undefined || !Number.isFinite(days) || days < 0) return null;
  let match: AgingTierConfig | null = null;
  AGING_TIERS.forEach((tier) => {
    if (days >= tier.minDays) match = tier;
  });
  return match;
}

// Minimum style age offered by the Age threshold filter.
export const AGE_THRESHOLD_OPTIONS: readonly number[] = [30, 60, 90, 180, 365];

export const ageThresholdLabel = (days: number | null): string => (days === null ? 'All ages' : `${days}+ days`);

// --- Date range ----------------------------------------------------------------------------------

// The sales window for Units Sold, Sell-Through % and Zero-sales; same presets as Top Sellers.
export const DATE_RANGE_PRESETS = TOP_SELLERS_DATE_RANGE_PRESETS;
export const DEFAULT_DATE_RANGE = DEFAULT_TOP_SELLERS_DATE_RANGE;
export const CUSTOM_DATE_RANGE = 'Custom range...';
export { resolveDateRange };

// --- Hierarchy, SKU grain, search ----------------------------------------------------------------

export type AgedInventoryHierarchy = 'total' | 'class' | 'vendor';
export type SkuGrain = 'product' | 'sku';
export type SearchField = 'product_name' | 'sku';

export const HIERARCHY_OPTIONS: readonly { key: AgedInventoryHierarchy; label: string; description: string; groupNoun: string }[] = [
  { key: 'total', label: 'Total by Product', description: 'One row per style. No grouping.', groupNoun: 'Total' },
  { key: 'class', label: 'Class', description: 'Collapsible group under each class, with subtotals.', groupNoun: 'Class' },
  { key: 'vendor', label: 'Vendor', description: 'Collapsible group under each vendor, with subtotals.', groupNoun: 'Vendor' },
];

export const SEARCH_FIELD_OPTIONS: readonly { key: SearchField; label: string; placeholder: string }[] = [
  { key: 'product_name', label: 'Product name', placeholder: 'Search product name...' },
  { key: 'sku', label: 'SKU', placeholder: 'Search SKU...' },
];

// Styles listed before "Show N more styles".
export const PAGE_SIZE = 25;

// --- Metrics -------------------------------------------------------------------------------------

/** Sell-Through % = units sold / (units sold + units on hand), as 0-100; null with neither. */
export function computeSellThroughPct(unitsSold: number, unitsOnHand: number): number | null {
  const sold = Math.max(0, Number(unitsSold) || 0);
  const onHand = Math.max(0, Number(unitsOnHand) || 0);
  const denominator = sold + onHand;
  return denominator > 0 ? (sold / denominator) * 100 : null;
}

// --- Size / color --------------------------------------------------------------------------------

/**
 * A SKU's color and size. The catalog's size and color fields are sparsely populated, so when both
 * are empty they are read from variant_title ("Indigo / 30"), which every SKU carries.
 */
export function resolveSkuColorSize(
  sku: Pick<AgedInventorySkuRow, 'color' | 'size' | 'variant_title'>
): { color: string | null; size: string | null } {
  const hasField = (value: string | null) => (value ?? '').trim() !== '';
  if (hasField(sku.color) || hasField(sku.size)) return normalizeVariantColorSize(sku.color, sku.size);
  const parts = (sku.variant_title ?? '').split('/').map((part) => part.trim()).filter(Boolean);
  if (parts.length === 0) return { color: null, size: null };
  if (parts.length === 1) return normalizeVariantColorSize(parts[0], null);
  return normalizeVariantColorSize(parts[0], parts.slice(1).join(' / '));
}

export const EMPTY_CELL = '—';

/** A style's Size or Color cell: the one value its SKUs share, "N sizes" / "N colors", or a dash. */
export function rollupLabel(values: (string | null)[], plural: string): string {
  const distinct = Array.from(new Set(values.filter((value): value is string => value !== null)));
  if (distinct.length === 0) return EMPTY_CELL;
  if (distinct.length === 1) return distinct[0];
  return `${distinct.length} ${plural}`;
}

// --- View model ----------------------------------------------------------------------------------

export interface AgedInventorySku extends AgedInventorySkuRow {
  id: string;
  size_label: string;
  color_label: string;
  sell_through_pct: number | null;
}

export interface AgedInventoryStyle extends Omit<AgedInventoryStyleRow, 'skus'> {
  id: string;
  size_label: string;
  color_label: string;
  sell_through_pct: number | null;
  skus: AgedInventorySku[];
}

export function mapSkuRow(row: AgedInventorySkuRow, styleId: string): AgedInventorySku {
  const { color, size } = resolveSkuColorSize(row);
  return {
    ...row,
    id: `${styleId}::${row.sku_id}`,
    color,
    size,
    color_label: color ?? EMPTY_CELL,
    size_label: size ?? EMPTY_CELL,
    sell_through_pct: computeSellThroughPct(row.units_sold, row.units_oh),
  };
}

export function mapStyleRow(row: AgedInventoryStyleRow): AgedInventoryStyle {
  const id = row.product_id;
  const skus = (row.skus ?? []).map((sku) => mapSkuRow(sku, id));
  return {
    ...row,
    id,
    skus,
    size_label: rollupLabel(skus.map((sku) => sku.size), 'sizes'),
    color_label: rollupLabel(skus.map((sku) => sku.color), 'colors'),
    sell_through_pct: computeSellThroughPct(row.units_sold, row.units_oh),
  };
}

/**
 * The note after a style's name: "3 sizes", or "2 colors" when its SKUs differ only by color,
 * else "N SKUs". Null for a single-SKU style.
 */
export function variantCountLabel(style: Pick<AgedInventoryStyle, 'skus'>): string | null {
  const count = style.skus.length;
  if (count <= 1) return null;
  const sizes = new Set(style.skus.map((sku) => sku.size).filter(Boolean)).size;
  const colors = new Set(style.skus.map((sku) => sku.color).filter(Boolean)).size;
  if (sizes > 1) return `${sizes} sizes`;
  if (colors > 1) return `${colors} colors`;
  return `${count} SKUs`;
}

/** The min–max under a style's age badge; null for one SKU or when every SKU is the same age. */
export function ageRangeLabel(style: Pick<AgedInventoryStyle, 'days_on_hand' | 'days_on_hand_min'>): string | null {
  const { days_on_hand: max, days_on_hand_min: min } = style;
  if (max === null || min === null || min === max) return null;
  return `${min}–${max} days`;
}

// --- Totals and groups ---------------------------------------------------------------------------

export interface AgedInventoryTotals {
  styleCount: number;
  units_oh: number;
  retail_value_oh: number;
  cost_value_oh: number;
  units_sold: number;
  sell_through_pct: number | null;
  first_received: string | null;
  last_received: string | null;
}

const minDate = (a: string | null, b: string | null) => (a === null ? b : b === null ? a : a < b ? a : b);
const maxDate = (a: string | null, b: string | null) => (a === null ? b : b === null ? a : a > b ? a : b);

/** Sums of the additive columns, with the earliest first and latest last receipt. */
export function computeTotals(styles: AgedInventoryStyle[]): AgedInventoryTotals {
  const totals = styles.reduce(
    (acc, style) => ({
      units_oh: acc.units_oh + (style.units_oh || 0),
      retail_value_oh: acc.retail_value_oh + (style.retail_value_oh || 0),
      cost_value_oh: acc.cost_value_oh + (style.cost_value_oh || 0),
      units_sold: acc.units_sold + (style.units_sold || 0),
      first_received: minDate(acc.first_received, style.first_received),
      last_received: maxDate(acc.last_received, style.last_received),
    }),
    { units_oh: 0, retail_value_oh: 0, cost_value_oh: 0, units_sold: 0, first_received: null as string | null, last_received: null as string | null }
  );
  return { ...totals, styleCount: styles.length, sell_through_pct: computeSellThroughPct(totals.units_sold, totals.units_oh) };
}

export interface AgedInventoryGroup {
  key: string;
  label: string;
  styles: AgedInventoryStyle[];
  totals: AgedInventoryTotals;
}

const NO_GROUP_VALUE = '(none)';

const groupValue = (style: AgedInventoryStyle, hierarchy: AgedInventoryHierarchy): string => {
  if (hierarchy === 'class') return style.class_name || NO_GROUP_VALUE;
  if (hierarchy === 'vendor') return style.vendor || NO_GROUP_VALUE;
  return '';
};

/**
 * Sorted styles grouped by the hierarchy's dimension, groups in order of their first (top-ranked)
 * style, so the oldest stock's group leads. A blank value groups under "(none)".
 */
export function groupStyles(styles: AgedInventoryStyle[], hierarchy: AgedInventoryHierarchy): AgedInventoryGroup[] {
  if (hierarchy === 'total') return [];
  const groups = new Map<string, AgedInventoryStyle[]>();
  styles.forEach((style) => {
    const key = groupValue(style, hierarchy);
    const list = groups.get(key);
    if (list) list.push(style);
    else groups.set(key, [style]);
  });
  return Array.from(groups.entries()).map(([key, list]) => ({ key, label: key, styles: list, totals: computeTotals(list) }));
}

// --- Columns -------------------------------------------------------------------------------------

export const PRODUCT_COLUMN_KEY = 'product_title';

export type AgedInventoryColumnKey =
  | 'vendor'
  | 'class_name'
  | 'size'
  | 'color'
  | 'units_oh'
  | 'retail_value_oh'
  | 'cost_value_oh'
  | 'first_received'
  | 'last_received'
  | 'days_on_hand'
  | 'units_sold'
  | 'sell_through_pct'
  | 'aging_tier';

export type AgedInventorySortKey = typeof PRODUCT_COLUMN_KEY | AgedInventoryColumnKey;

type AgedInventoryRow = AgedInventoryStyle | AgedInventorySku;

export interface AgedInventoryColumnConfig {
  key: AgedInventoryColumnKey;
  label: string;
  description: string;
  align: 'left' | 'right' | 'center';
  sortType: 'text' | 'numeric';
  width: number;
  // One line under the column's name in the Columns popover.
  helper: string;
  // Shown only with Show SKUs on SKU; at product grain the note after the name carries the count.
  skuOnly?: boolean;
}

// The columns after the sticky Product column, in design order.
export const AGED_INVENTORY_COLUMNS: readonly AgedInventoryColumnConfig[] = [
  { key: 'vendor', label: 'Vendor', helper: 'Vendor supplying the style', description: 'Vendor supplying this product.', align: 'left', sortType: 'text', width: 170 },
  { key: 'class_name', label: 'Class', helper: 'Merchandise plan class', description: 'Merchandise plan class.', align: 'left', sortType: 'text', width: 140 },
  { key: 'size', label: 'Size', helper: 'If available from your POS', description: 'The SKU size. A style shows its one size or the number of sizes.', align: 'left', sortType: 'text', width: 100, skuOnly: true },
  { key: 'color', label: 'Color', helper: 'If available from your POS', description: 'The SKU color. A style shows its one color or the number of colors.', align: 'left', sortType: 'text', width: 110, skuOnly: true },
  { key: 'units_oh', label: 'Units OH', helper: 'Units on hand now', description: 'Units on hand in the current inventory snapshot.', align: 'right', sortType: 'numeric', width: 110 },
  { key: 'retail_value_oh', label: 'Retail Value OH', helper: 'On hand at retail', description: 'On-hand units valued at retail.', align: 'right', sortType: 'numeric', width: 140 },
  { key: 'cost_value_oh', label: 'Cost Value OH', helper: 'On hand at cost', description: 'On-hand units valued at cost.', align: 'right', sortType: 'numeric', width: 130 },
  { key: 'first_received', label: 'First Received', helper: 'Earliest receipt into inventory', description: 'Date first received into inventory. A style shows its earliest SKU.', align: 'left', sortType: 'numeric', width: 130 },
  { key: 'last_received', label: 'Last Received', helper: 'Most recent receipt', description: 'Most recent receipt into inventory. A style shows its latest SKU.', align: 'left', sortType: 'numeric', width: 130 },
  { key: 'days_on_hand', label: 'Age (days)', helper: 'Days since first received', description: 'Days since first received. A style shows its oldest SKU, with the range across its SKUs. Blank when no receive date is available.', align: 'center', sortType: 'numeric', width: 130 },
  { key: 'units_sold', label: 'Units Sold', helper: 'Sold in the date range', description: 'Units sold in the selected date range.', align: 'right', sortType: 'numeric', width: 110 },
  { key: 'sell_through_pct', label: 'Sell-Through %', helper: 'Sold / (sold + on hand)', description: 'Units sold / (units sold + units on hand), over the selected date range.', align: 'right', sortType: 'numeric', width: 130 },
  { key: 'aging_tier', label: 'Aging Tier', helper: '0–90, 91–180, 181–365, 366+ days', description: 'Aging bucket from age: 0–90, 91–180, 181–365 and 366+ days.', align: 'center', sortType: 'numeric', width: 120 },
];

// Under the Columns popover's list.
export const COLUMNS_POPOVER_NOTE = 'Drag column headers to reorder them in the table.';

export const PRODUCT_COLUMN_LABEL = 'Product';
export const PRODUCT_COLUMN_DESCRIPTION = 'Product style, with how many sizes or colors it carries. Switch Show SKUs to SKU for each one.';
export const PRODUCT_COLUMN_WIDTH = 280;

export const DEFAULT_COLUMN_ORDER: readonly AgedInventoryColumnKey[] = AGED_INVENTORY_COLUMNS.map((col) => col.key);
export const DEFAULT_COLUMN_WIDTHS: Readonly<Record<string, number>> = {
  [PRODUCT_COLUMN_KEY]: PRODUCT_COLUMN_WIDTH,
  ...Object.fromEntries(AGED_INVENTORY_COLUMNS.map((col) => [col.key, col.width])),
};
export const METRIC_COLUMN_WIDTH = 120;

const COLUMN_BY_KEY = new Map<AgedInventoryColumnKey, AgedInventoryColumnConfig>(AGED_INVENTORY_COLUMNS.map((col) => [col.key, col]));

export const columnByKey = (key: AgedInventoryColumnKey): AgedInventoryColumnConfig | undefined => COLUMN_BY_KEY.get(key);

export const isColumnShown = (col: AgedInventoryColumnConfig, skuGrain: SkuGrain, visible: Record<string, boolean>): boolean =>
  visible[col.key] !== false && (!col.skuOnly || skuGrain === 'sku');

export const sortTypeOf = (key: AgedInventorySortKey): 'text' | 'numeric' =>
  key === PRODUCT_COLUMN_KEY ? 'text' : COLUMN_BY_KEY.get(key)?.sortType ?? 'numeric';

export const sortKeyLabel = (key: AgedInventorySortKey): string =>
  key === PRODUCT_COLUMN_KEY ? PRODUCT_COLUMN_LABEL : COLUMN_BY_KEY.get(key)?.label ?? key;

const isStyle = (row: AgedInventoryRow): row is AgedInventoryStyle => 'skus' in row;

/** The value a column sorts by; null sorts last in either direction. */
export function sortValue(row: AgedInventoryRow, key: AgedInventorySortKey): string | number | null {
  switch (key) {
    case 'product_title':
      return isStyle(row) ? row.product_title : row.variant_title ?? row.sku_id;
    case 'vendor':
      return isStyle(row) ? row.vendor : null;
    case 'class_name':
      return isStyle(row) ? row.class_name : null;
    case 'size':
      return row.size_label === EMPTY_CELL ? null : row.size_label;
    case 'color':
      return row.color_label === EMPTY_CELL ? null : row.color_label;
    case 'first_received':
      return row.first_received;
    case 'last_received':
      return row.last_received;
    // A tier is a band of age, so both sort by the age itself.
    case 'days_on_hand':
    case 'aging_tier':
      return row.days_on_hand;
    case 'units_oh':
    case 'retail_value_oh':
    case 'cost_value_oh':
    case 'units_sold':
    case 'sell_through_pct':
      return row[key];
    default:
      return null;
  }
}

export interface SortConfig {
  key: AgedInventorySortKey;
  direction: 'asc' | 'desc';
}

// Oldest first, as the report opens.
export const DEFAULT_SORT: SortConfig = { key: 'days_on_hand', direction: 'desc' };

const isBlank = (value: string | number | null | undefined): boolean =>
  value === null || value === undefined || value === '' || (typeof value === 'number' && !Number.isFinite(value));

/** Compares two values in `direction`, with blanks (unknown age, no receive date) always last. */
export function compareNullsLast(
  a: string | number | null | undefined,
  b: string | number | null | undefined,
  direction: 'asc' | 'desc'
): number {
  const aBlank = isBlank(a);
  const bBlank = isBlank(b);
  if (aBlank || bBlank) return aBlank === bBlank ? 0 : aBlank ? 1 : -1;
  const result =
    typeof a === 'number' && typeof b === 'number'
      ? a - b
      : String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  return direction === 'asc' ? result : -result;
}

/** Styles in sort order, each style's SKUs sorted the same way. Ties keep the server's order. */
export function sortStyles(styles: AgedInventoryStyle[], sort: SortConfig): AgedInventoryStyle[] {
  const sortRows = <T extends AgedInventoryRow>(rows: T[]): T[] =>
    rows
      .map((row, index) => ({ row, index }))
      .sort((a, b) => compareNullsLast(sortValue(a.row, sort.key), sortValue(b.row, sort.key), sort.direction) || a.index - b.index)
      .map(({ row }) => row);
  return sortRows(styles).map((style) => ({ ...style, skus: sortRows(style.skus) }));
}

/** Search box: product name, or any of the style's SKU ids / variant titles. */
export function matchesSearch(style: AgedInventoryStyle, query: string, field: SearchField = 'product_name'): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const fields = field === 'sku' ? style.skus.flatMap((sku) => [sku.sku_id, sku.variant_title]) : [style.product_title];
  return fields.some((value) => (value ?? '').toLowerCase().includes(q));
}

// --- Formatting ----------------------------------------------------------------------------------

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const unitsFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

export const formatCurrency = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value) ? EMPTY_CELL : currencyFormatter.format(value);

export const formatUnits = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value) ? EMPTY_CELL : unitsFormatter.format(value);

export const formatPercent = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value) ? EMPTY_CELL : `${value.toFixed(1)}%`;

/** "Aug 27, 2025" from an ISO date; a dash when missing or unreadable. */
export const formatDate = (value: string | null | undefined): string => {
  if (!value) return EMPTY_CELL;
  const parsed = parseISO(value);
  return isValid(parsed) ? format(parsed, 'MMM d, yyyy') : EMPTY_CELL;
};

/** "Jul 15 – Oct 12, 2026", or with both years when the range spans two. */
export function formatDateRange(from: string, to: string): string {
  const start = parseISO(from);
  const end = parseISO(to);
  if (!isValid(start) || !isValid(end)) return `${from} – ${to}`;
  const startFormat = start.getFullYear() === end.getFullYear() ? 'MMM d' : 'MMM d, yyyy';
  return `${format(start, startFormat)} – ${format(end, 'MMM d, yyyy')}`;
}

/** Plain-text value of a cell, shared by the table's text cells. */
export function cellText(row: AgedInventoryRow, key: AgedInventoryColumnKey): string {
  switch (key) {
    case 'vendor':
      return isStyle(row) ? row.vendor ?? EMPTY_CELL : EMPTY_CELL;
    case 'class_name':
      return isStyle(row) ? row.class_name ?? EMPTY_CELL : EMPTY_CELL;
    case 'size':
      return row.size_label;
    case 'color':
      return row.color_label;
    case 'units_oh':
    case 'units_sold':
      return formatUnits(row[key]);
    case 'retail_value_oh':
    case 'cost_value_oh':
      return formatCurrency(row[key]);
    case 'first_received':
    case 'last_received':
      return formatDate(row[key]);
    case 'days_on_hand':
      return row.days_on_hand === null ? '' : String(row.days_on_hand);
    case 'sell_through_pct':
      return formatPercent(row.sell_through_pct);
    case 'aging_tier':
      return getAgingTier(row.days_on_hand)?.name ?? '';
    default:
      return '';
  }
}

/** A Total / group row's cell: sums and receive dates; non-additive columns show a dash. */
export function totalsCellText(totals: AgedInventoryTotals, key: AgedInventoryColumnKey): string {
  switch (key) {
    case 'units_oh':
    case 'units_sold':
      return formatUnits(totals[key]);
    case 'retail_value_oh':
    case 'cost_value_oh':
      return formatCurrency(totals[key]);
    case 'first_received':
    case 'last_received':
      return formatDate(totals[key]);
    case 'sell_through_pct':
      return formatPercent(totals.sell_through_pct);
    default:
      return EMPTY_CELL;
  }
}

// CSV holds raw numbers and ISO dates (no $ or separators) so a spreadsheet can total and sort them.
function csvValue(row: AgedInventoryRow, key: AgedInventoryColumnKey): string {
  switch (key) {
    case 'units_oh':
    case 'units_sold':
    case 'retail_value_oh':
    case 'cost_value_oh':
      return Number.isFinite(row[key]) ? String(Math.round(row[key] * 100) / 100) : '';
    case 'sell_through_pct':
      return row.sell_through_pct === null ? '' : row.sell_through_pct.toFixed(1);
    case 'first_received':
    case 'last_received':
      return row[key] ?? '';
    default: {
      const text = cellText(row, key);
      return text === EMPTY_CELL ? '' : text;
    }
  }
}

const TEXT_COLUMNS: readonly AgedInventoryColumnKey[] = ['vendor', 'class_name', 'size', 'color', 'aging_tier'];

export interface AgedInventoryCsvOptions {
  columns: readonly AgedInventoryColumnConfig[];
  // SKU lines follow each style at SKU grain.
  includeSkus: boolean;
  // The hierarchy's group column ("Category"), or null for Total by Product.
  groupHeader: string | null;
  groupOf?: (style: AgedInventoryStyle) => string;
}

/**
 * The export: every style the filters return (not just the page shown), in on-screen order with
 * the visible columns. SKU lines repeat the style's product, vendor and class so each stands alone.
 */
export function buildAgedInventoryCsv(styles: AgedInventoryStyle[], { columns, includeSkus, groupHeader, groupOf }: AgedInventoryCsvOptions): string {
  const lead = groupHeader ? [groupHeader] : [];
  const header = [...lead, 'Row Type', PRODUCT_COLUMN_LABEL, 'SKU', ...columns.map((col) => col.label)].map((h) => csvCell(h, true));
  const lines = [header.join(',')];
  styles.forEach((style) => {
    const groupCells = groupHeader && groupOf ? [csvCell(groupOf(style), true)] : [];
    const styleCells = columns.map((col) => csvCell(csvValue(style, col.key), TEXT_COLUMNS.includes(col.key)));
    lines.push([...groupCells, csvCell('Style', true), csvCell(style.product_title, true), '', ...styleCells].join(','));
    if (!includeSkus) return;
    style.skus.forEach((sku) => {
      const skuCells = columns.map((col) => {
        if (col.key === 'vendor' || col.key === 'class_name') return csvCell(csvValue(style, col.key), true);
        return csvCell(csvValue(sku, col.key), TEXT_COLUMNS.includes(col.key));
      });
      lines.push([...groupCells, csvCell('SKU', true), csvCell(style.product_title, true), csvCell(sku.sku_id, true), ...skuCells].join(','));
    });
  });
  return lines.join('\n');
}

export const groupValueOf = groupValue;

// --- Saved views ---------------------------------------------------------------------------------

export interface AgedInventoryFilterState {
  dateRange: string;
  customStartDate: string;
  customEndDate: string;
  locations: string[];
  excludeLocations: boolean;
  classes: string[];
  excludeClasses: boolean;
  vendors: string[];
  excludeVendors: boolean;
  categories: string[];
  excludeCategories: boolean;
  ageThreshold: number | null;
  agingTier: AgingTierKey | null;
  zeroSalesOnly: boolean;
  excludeCore: boolean;
}

export const DEFAULT_FILTERS: AgedInventoryFilterState = {
  dateRange: DEFAULT_DATE_RANGE,
  customStartDate: '',
  customEndDate: '',
  locations: [],
  excludeLocations: false,
  classes: [],
  excludeClasses: false,
  vendors: [],
  excludeVendors: false,
  categories: [],
  excludeCategories: false,
  ageThreshold: null,
  agingTier: null,
  zeroSalesOnly: false,
  excludeCore: false,
};

export interface AgedInventoryViewPayload {
  filters: AgedInventoryFilterState;
  hierarchy: AgedInventoryHierarchy;
  skuGrain: SkuGrain;
  visibleColumns: AgedInventoryColumnKey[];
  columnOrder: AgedInventoryColumnKey[];
  sort: SortConfig;
}

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

const SORT_KEYS: readonly AgedInventorySortKey[] = [PRODUCT_COLUMN_KEY, ...DEFAULT_COLUMN_ORDER];

/**
 * A saved view's payload made safe to apply: unknown columns, tiers, thresholds or presets are
 * dropped and missing fields fall back to the defaults, so an older or hand-edited view can't break
 * the report.
 */
export function normalizeViewPayload(raw: unknown): AgedInventoryViewPayload {
  const payload = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const filters = (payload.filters && typeof payload.filters === 'object' ? payload.filters : {}) as Record<string, unknown>;
  const known = (keys: string[]) => keys.filter((key): key is AgedInventoryColumnKey => COLUMN_BY_KEY.has(key as AgedInventoryColumnKey));
  const tier = typeof filters.agingTier === 'string' && TIER_BY_KEY.has(filters.agingTier as AgingTierKey) ? (filters.agingTier as AgingTierKey) : null;
  const threshold = typeof filters.ageThreshold === 'number' && AGE_THRESHOLD_OPTIONS.includes(filters.ageThreshold) ? filters.ageThreshold : null;
  const dateRange = (DATE_RANGE_PRESETS as readonly string[]).includes(asString(filters.dateRange)) ? asString(filters.dateRange) : DEFAULT_DATE_RANGE;
  const sort = (payload.sort && typeof payload.sort === 'object' ? payload.sort : {}) as Record<string, unknown>;
  const sortKey = SORT_KEYS.includes(sort.key as AgedInventorySortKey) ? (sort.key as AgedInventorySortKey) : DEFAULT_SORT.key;
  const sortDirection = sort.direction === 'asc' || sort.direction === 'desc' ? sort.direction : DEFAULT_SORT.direction;
  const hierarchy = HIERARCHY_OPTIONS.some((opt) => opt.key === payload.hierarchy) ? (payload.hierarchy as AgedInventoryHierarchy) : 'total';

  return {
    filters: {
      dateRange,
      customStartDate: asString(filters.customStartDate),
      customEndDate: asString(filters.customEndDate),
      locations: asStringArray(filters.locations),
      excludeLocations: filters.excludeLocations === true,
      classes: asStringArray(filters.classes),
      excludeClasses: filters.excludeClasses === true,
      vendors: asStringArray(filters.vendors),
      excludeVendors: filters.excludeVendors === true,
      categories: asStringArray(filters.categories),
      excludeCategories: filters.excludeCategories === true,
      ageThreshold: threshold,
      agingTier: tier,
      zeroSalesOnly: filters.zeroSalesOnly === true,
      excludeCore: filters.excludeCore === true,
    },
    hierarchy,
    skuGrain: payload.skuGrain === 'sku' ? 'sku' : 'product',
    visibleColumns: Array.isArray(payload.visibleColumns) ? known(asStringArray(payload.visibleColumns)) : [...DEFAULT_COLUMN_ORDER],
    columnOrder: known(asStringArray(payload.columnOrder)),
    sort: { key: sortKey, direction: sortDirection },
  };
}

type StorageUserId = string | number | null | undefined;
const storageScope = (userId: StorageUserId, clientId: string | number) => `${userId ?? 'anonymous'}-${clientId}`;

export const AGED_INVENTORY_STORAGE_PREFIX = 'aged-inventory';
export const getColumnOrderStorageKey = (userId: StorageUserId, clientId: string | number) =>
  `${AGED_INVENTORY_STORAGE_PREFIX}-column-order-${storageScope(userId, clientId)}`;
export const getColumnWidthsStorageKey = (userId: StorageUserId, clientId: string | number) =>
  `${AGED_INVENTORY_STORAGE_PREFIX}-column-widths-${storageScope(userId, clientId)}`;
export const getPinnedColumnsStorageKey = (userId: StorageUserId, clientId: string | number) =>
  `${AGED_INVENTORY_STORAGE_PREFIX}-pinned-columns-${storageScope(userId, clientId)}`;
